import classNames from 'classnames';
import Button from 'components/button/button';
import Hero from 'components/heroes/hero';
import Page from 'components/page/page';
import Text from 'components/text/text';
import TransitionLink from 'components/transition-link/transition-link';
import Endpoints from 'helpers/enums/endpoints';
import { validateEmail } from 'helpers/utilities/validators';
import MakeHash from 'make-hash';
import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import { validatePassword } from 'utils/password';
import messages from './messages';

class CreateAccount extends Component {
    constructor(props) {
        super(props);

        const { intl } = props;

        this.state = {
            serverError: false,
            serverMessages: [],
            loaderActive: false,
            loading: false,
            email: '',
            emailError: '',
            client: 'ride',
            locale: intl.locale,
            firstName: '',
            lastName: '',
            password: '',
            passwordError: '',
            acceptTAC: true,
            acceptTOU: false,
            marketingOptin: false,
            submitEnabled: false,
            formMessages: [],
            showForm: true,
        };

        this.page = {
            heading: intl.formatMessage(messages.create_account_heading),
            intro: intl.formatMessage(messages.create_account_intro),
        };

        this.primaryAction = {
            label: intl.formatMessage(messages.submit_label),
        };

        this.secondaryAction = {
            path: 'sign-in',
            label: intl.formatMessage(messages.sign_in_label),
        };

        this.fields = [];

        this.setUpHandlers();
    }

    /**
     * Binds any handlers to the correct context
     *
     * @method  setUpHandlers
     */
    setUpHandlers() {
        this.handleSubmit = this.handleSubmit.bind(this);
        this.updatePassword = this.updatePassword.bind(this);
        this.handleError = this.handleError.bind(this);
    }

    trackField(field) {
        if (!field) {
            return;
        }
        this.fields.push(field);
    }

    handleSubmit(event) {
        // TODO : check validation
        // const isValid = !this.formHasErrors();

        if (event) event.preventDefault();

        this.createAccount();

        // TODO: remove
        if (isValid) {
            this.createAccount();
        }
    }

    formHasErrors() {
        let errors = this.fields.filter((field) => {
            const hasError =
                field.state.isError || field.input.value.trim().length === 0;

            if (hasError) {
                field.setState({ isError: true });
            }

            return hasError;
        });

        return errors.length > 0;
    }

    /**
     * Attempts to log a user in via Auth API
     *
     * @method  createAccount
     */
    createAccount() {
        this.setState({ loading: true });

        this.props.apiService
            .post(
                Endpoints.register,
                {
                    client: 'rider',
                    email: this.state.email,
                    password: this.state.password,
                    first_name: this.state.firstName,
                    last_name: this.state.lastName,
                    locale: this.props.intl.locale,
                    tou: this.state.acceptTOU,
                    retul_marketing_opt_in: this.state.marketingOptin,
                },
                this.handleError
            )
            .then((data) => {
                if (typeof data === 'undefined') return;
                this.setState({
                    formMessages: [
                        this.props.intl.formatMessage(messages.USER_CREATED),
                        this.props.intl.formatMessage(
                            messages.USER_CREATED_VERIFY_INSTRUCTIONS
                        ),
                    ],
                    showForm: false,
                    loading: false,
                });
            });
    }

    _renderHero() {
        return (
            <Hero title={this.page.heading} supportingTxt={this.page.intro} />
        );
    }

    checkPasswordComplexity(event) {
        if (!validatePassword(this.state.password)) {
            this.setState({
                passwordError:
                    'Passwords must be at least 8 characters long, include upper- and lower-case letters, and include a number',
            });
        } else {
            this.setState({ passwordError: '' });
        }
    }

    _renderFormMessages() {
        const ulClasses = classNames('form-messages', {
            'form-messages_hasMessage': this.state.formMessages.length > 0,
        });
        const color = this.state.serverError ? 'DefaultOrange' : 'DefaultBlue';

        return (
            <ul className={ulClasses}>
                {this.state.formMessages.map((msg, i) => {
                    if (typeof msg == 'string') {
                        return (
                            <li key={MakeHash(`${msg}-${i}`)}>
                                <Text color={color} weight="light">
                                    {msg.endsWith('.') ? msg : msg + '.'}
                                </Text>
                            </li>
                        );
                    } else {
                        return <li key={MakeHash(`${i}`)}>{msg}</li>;
                    }
                })}
            </ul>
        );
    }

    handleError(error) {
        this.setState({
            loading: false,
        });

        error.response.json().then((errorBody) => {
            // Try to translate first error
            // EMAIL_NOT_VERIFIED, or USER_NOT_ACCEPTED_TERMS or INVALID_USERNAME_OR_PASSWORD
            if (errorBody.errors) {
                const firstError = errorBody.errors[0];
                const message = messages[firstError.message];

                let missingField = '';
                if (firstError.message === 'REQUIRED_FIELD') {
                    missingField = firstError.field;
                }

                // errors may look like  so handle if they are present in our messages
                if (message) {
                    return this.setState({
                        serverError: true,
                        formMessages: [
                            this.props.intl.formatMessage(message, {
                                field: missingField,
                            }),
                        ],
                    });
                }
            }

            // Use reason from the server if were unable to find error enum in our locales
            if (errorBody.reason) {
                return this.setState({
                    serverError: true,
                    formMessages: [errorBody.reason],
                });
            }

            // Use untranslated "non_field_errors" if that is present
            if (errorBody.non_field_errors) {
                return this.setState({
                    serverError: true,
                    formMessages: errorBody.non_field_errors,
                });
            }

            // Use a final catch-all error if none of the above applied
            this.setState({
                serverError: true,
                formMessages: ['Uknown error. Please try again.'],
            });
        });
    }

    updatePassword(event) {
        // Check that confirm password matches only when it has had entry
        event.persist();
        this.setState({ password: event.target.value }, () => {
            if (this.state.password.length > 0) {
                this.checkPasswordComplexity(event);
            }
        });
    }

    render() {
        const { intl } = this.props;
        const pageHero = this._renderHero();

        const emailError = !validateEmail(this.state.email);

        const passwordFieldClass = classNames('field field_floatingLabel', {
            field_hasError: this.state.passwordError,
        });

        const emailFieldClass = classNames('field field_floatingLabel', {
            field_hasError: this.state.emailError,
        });

        const firstNameError = this.state.firstName.length === 0;
        const lastNameError = this.state.lastName.length === 0;

        const formMessages = this._renderFormMessages();

        const submitEnabled =
            this.state.passwordError.length === 0 &&
            this.state.password.length > 0 &&
            this.state.acceptTOU &&
            this.state.firstName.length > 0 &&
            this.state.lastName.length > 0 &&
            this.state.email.length > 0;

        // const showForm = classNames('set', {
        //     hidden: !this.state.showForm,
        // });

        const showForm = this.state.showForm ? 'set' : 'hidden';

        return (
            <Page
                fixedLoader
                title={this.page.heading}
                pageHero={pageHero}
                isLoading={this.state.loading}
            >
                <div className="container container_constrainedMd">
                    <form onSubmit={this.handleSubmit} noValidate>
                        {formMessages}
                        <div className={showForm}>
                            <div className="set-hd">
                                <label className="field-label" htmlFor="email">
                                    {intl.formatMessage(messages.email_label)}
                                </label>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    className="field-input"
                                    label={intl.formatMessage(
                                        messages.email_label
                                    )}
                                    onChange={(e) => {
                                        this.setState({
                                            email: e.target.value,
                                        });
                                    }}
                                />
                                {emailError && (
                                    <span>
                                        {intl.formatMessage(
                                            messages.email_required_label
                                        )}
                                    </span>
                                )}
                            </div>
                            <div className="set-bd set-bd_super">
                                <div className={passwordFieldClass}>
                                    <label
                                        className="field-label"
                                        htmlFor="password"
                                    >
                                        {intl.formatMessage(
                                            messages.password_label
                                        )}
                                    </label>
                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        className="field-input"
                                        value={this.state.password}
                                        onChange={this.updatePassword}
                                    />
                                    <span
                                        className={
                                            passwordFieldClass && 'field-error'
                                        }
                                    >
                                        {this.state.passwordError}
                                    </span>
                                </div>
                            </div>
                            <div className="set-bd set-bd_super">
                                <div className={passwordFieldClass}>
                                    <label
                                        className="field-label"
                                        htmlFor="first-name"
                                    >
                                        {intl.formatMessage(
                                            messages.first_name_label
                                        )}
                                    </label>
                                    <input
                                        id="first-name"
                                        name="first name"
                                        className="field-input"
                                        value={this.state.firstName}
                                        onChange={(e) => {
                                            this.setState({
                                                firstName: e.target.value,
                                            });
                                        }}
                                    />
                                    {firstNameError && (
                                        <span>
                                            {intl.formatMessage(
                                                messages.first_name_required_label
                                            )}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="set-bd set-bd_super">
                                <div className={passwordFieldClass}>
                                    <label
                                        className="field-label"
                                        htmlFor="last-name"
                                    >
                                        {intl.formatMessage(
                                            messages.last_name_label
                                        )}
                                    </label>
                                    <input
                                        id="last-name"
                                        name="last name"
                                        className="field-input"
                                        value={this.state.lastName}
                                        onChange={(e) => {
                                            this.setState({
                                                lastName: e.target.value,
                                            });
                                        }}
                                    />
                                    {lastNameError && (
                                        <span>
                                            {intl.formatMessage(
                                                messages.last_name_required_label
                                            )}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="set-bd set-bd_super">
                                <div className={passwordFieldClass}>
                                    <input
                                        id="accept-tou"
                                        name="accept terms of use"
                                        type="checkbox"
                                        value={this.state.acceptTOU}
                                        onChange={(e) => {
                                            this.setState({
                                                acceptTOU: !this.state
                                                    .acceptTOU,
                                            });
                                        }}
                                    />
                                    <label htmlFor="accept-tou">
                                        {intl.formatMessage(
                                            messages.accept_tou_label
                                        )}
                                    </label>
                                    {!this.state.acceptTOU && (
                                        <span>
                                            <a
                                                target="_blank"
                                                className="txt txt_reg mix-txt_colorDefaultBlue"
                                                href="https://www.specialized.com/terms-of-use"
                                            >
                                                &nbsp;&nbsp;(required)
                                            </a>
                                        </span>
                                    )}
                                </div>

                                <div className={passwordFieldClass}>
                                    <input
                                        id="marketing-optin"
                                        name="accept marketing optin"
                                        type="checkbox"
                                        value={this.state.marketingOptin}
                                        onChange={(e) => {
                                            this.setState({
                                                marketingOptin: !this.state
                                                    .marketingOptin,
                                            });
                                        }}
                                    />
                                    <label htmlFor="marketing-optin">
                                        {intl.formatMessage(
                                            messages.marketing_optin_label
                                        )}
                                    </label>
                                </div>
                                <div className={passwordFieldClass}>
                                    <label htmlFor="accept-privacy-policy">
                                        {intl.formatMessage(
                                            messages.accept_privacy_policy_label
                                        )}
                                        &nbsp;
                                        <a
                                            target="_blank"
                                            className="txt txt_reg mix-txt_colorDefaultBlue"
                                            href="https://www.specialized.com/privacy-policy"
                                        >
                                            {intl.formatMessage(
                                                messages.accept_privacy_policy_link_text
                                            )}
                                        </a>
                                    </label>
                                </div>
                            </div>
                            <div className="set-bd set-bd_super">
                                <div className="set mix-set_alignCenter">
                                    <div className="set-hd">
                                        <Button
                                            animateOnClick
                                            onClick={this.handleSubmit}
                                            type="submit"
                                            className="btn_fixedWidth"
                                            disabled={!submitEnabled}
                                        >
                                            {this.primaryAction.label}
                                        </Button>
                                    </div>
                                    <div className="set-bd set-bd_separated">
                                        <TransitionLink
                                            className="txt txt_reg mix-txt_colorDefaultBlue"
                                            to={this.secondaryAction.path}
                                        >
                                            {this.secondaryAction.label}
                                        </TransitionLink>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </Page>
        );
    }
}

export default injectIntl(CreateAccount);



// WEBPACK FOOTER //
// ./src/client/pages/auth/create-account.jsx